import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useTypewriter } from '../../../hooks/useTypewriter.ts';
import AIAgentOrbLoader from '../../Dashboard/AIAgentOrb/AIAgentOrbLoader.tsx';
import styles from './ConversationalOnboarding.module.scss';

interface AgentMessageProps {
  content: string;
  isTyping: boolean;
  messageId?: string;
  onTypingComplete?: (messageId?: string) => void;
}

const AgentMessage: React.FC<AgentMessageProps> = ({ content, isTyping, messageId, onTypingComplete }) => {
  const { displayedText, isTyping: isTypingText } = useTypewriter(content, isTyping, 30);
  const lastCompleteRef = useRef<string | null>(null);
  const textRef = useRef<HTMLHeadingElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!textRef.current) return;
    const nextHeight = textRef.current.scrollHeight;
    setContentHeight(nextHeight);
  }, [displayedText, content]);

  useEffect(() => {
    if (!isTyping) {
      lastCompleteRef.current = null;
      return;
    }
    const completeKey = messageId || content;
    if (!isTypingText && displayedText === content && onTypingComplete && lastCompleteRef.current !== completeKey) {
      lastCompleteRef.current = completeKey;
      onTypingComplete(messageId);
    }
  }, [isTyping, isTypingText, displayedText, content, messageId, onTypingComplete]);

  if (!content) {
    return null;
  }

  return (
    <motion.div
      className={styles.agentMessageContainer}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      key={content}
    >
      <AIAgentOrbLoader 
        status={isTypingText ? 'thinking' : 'idle'} 
        className={styles.agentOrb}
      />
      <div
        className={styles.agentMessageContent}
        style={contentHeight ? { height: `${contentHeight}px` } : undefined}
      >
        <h1 className={styles.agentMessageText} ref={textRef}>
          {displayedText}
          {isTypingText && <span className={styles.cursor}>|</span>}
        </h1>
      </div>
    </motion.div>
  );
};

export default AgentMessage;
